import { AlertCircle, ClipboardList, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import { useAuth } from "@/app/context/Auth/AuthContext";
import { cn } from "@/app/handlers/cn.utils";

import Loader from "../../components/Loader";
import Header from "../Profile/components/Header";
import FitCheckCard from "./components/FitCheckCard";
import FitCheckDetailDrawer from "./components/FitCheckDetailDrawer";
import FitCheckStats from "./components/FitCheckStats";
import { AssessmentRecord, useAllAnswers, useAssessments } from "./config/api";
import { resolveFit } from "./config/stats";

type FilterTab = "all" | "strong" | "attention";

const FILTER_DOTS: Record<FilterTab, string | null> = {
	all: null,
	strong: "bg-green-500",
	attention: "bg-amber-500",
};

function FitCheckDashboard() {
	const navigate = useNavigate();
	const { isLoggedIn, userData } = useAuth();
	const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
	const [selectedRecord, setSelectedRecord] = useState<AssessmentRecord | null>(null);
	const [isScrolled, setIsScrolled] = useState(false);

	const userId = userData?.userID || "";
	const { data: records, isLoading, isError } = useAssessments(userId);

	// One request for every check's answers, so each card's bar can reflect what the user answered.
	// Once loaded, a job absent from the map has no answers ([]) — not a still-loading unknown.
	const { data: answersByJob } = useAllAnswers(userId);
	const answersFor = (jobId: string) => (answersByJob ? (answersByJob[jobId] ?? []) : undefined);

	useEffect(() => {
		const handleScroll = () => setIsScrolled(window.scrollY > 0);
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// A strong fit is one the user has sorted through where what they have covers the role. Everything
	// else — partial, weak, or still-unanswered — needs attention. Same verdict the card badge shows.
	const strongJobs = useMemo(() => {
		const set = new Set<string>();
		(records ?? []).forEach((record) => {
			if (resolveFit(record.assessment, answersByJob?.[record.jobId]).verdict === "strong") set.add(record.jobId);
		});
		return set;
	}, [records, answersByJob]);

	const filterCounts = useMemo(() => {
		if (!records) return { all: 0, strong: 0, attention: 0 };
		return { all: records.length, strong: strongJobs.size, attention: records.length - strongJobs.size };
	}, [records, strongJobs]);

	const filteredRecords = useMemo(() => {
		if (!records) return [];
		return records.filter((record) => {
			if (activeFilter === "all") return true;
			return activeFilter === "strong" ? strongJobs.has(record.jobId) : !strongJobs.has(record.jobId);
		});
	}, [records, activeFilter, strongJobs]);

	const filterTabs: { key: FilterTab; label: string; count: number }[] = [
		{ key: "all", label: "All", count: filterCounts.all },
		{ key: "strong", label: "Strong fit", count: filterCounts.strong },
		{ key: "attention", label: "Needs attention", count: filterCounts.attention },
	];

	if (!isLoggedIn) return <Navigate to="/signin" replace />;

	const hasRecords = !!records && records.length > 0;

	return (
		<>
			<FitCheckDetailDrawer record={selectedRecord} onClose={() => setSelectedRecord(null)} />

			<div className={cn("sticky top-0 z-50 bg-white transition-shadow duration-300", isScrolled && "shadow-xs")}>
				<Header variant="applicant" />
			</div>

			<div className="min-h-screen bg-gray-50">
				<div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
					<div className="mb-6 flex items-center justify-between gap-3">
						<h1 className="font-plus-jakarta text-xl font-bold text-gray-900">Your FitChecks</h1>

						<Button size="sm" variant="outline" onClick={() => navigate("/jobs")} className="shrink-0">
							<Plus className="mr-1.5 h-3.5 w-3.5" />
							New FitCheck
						</Button>
					</div>

					<FitCheckStats />

					{hasRecords && (
						<div className="mb-6 inline-flex max-w-full flex-wrap items-center gap-1 rounded-lg bg-gray-100 p-1">
							{filterTabs.map((tab) => {
								const isActive = activeFilter === tab.key;
								const dot = FILTER_DOTS[tab.key];
								return (
									<button
										key={tab.key}
										onClick={() => setActiveFilter(tab.key)}
										className={cn(
											"flex items-center gap-2 whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors",
											isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800",
										)}
									>
										{dot && <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />}
										{tab.label}
										<span className="text-xs tabular-nums text-gray-400">{tab.count}</span>
									</button>
								);
							})}
						</div>
					)}

					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<Loader />
						</div>
					) : isError ? (
						<div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
							<AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
							<p className="text-red-700">Failed to load FitCheck history. Please try again.</p>
							<Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
								Retry
							</Button>
						</div>
					) : filteredRecords.length === 0 ? (
						<div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
							<div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
								<ClipboardList className="h-8 w-8 text-gray-400" />
							</div>
							<h3 className="mb-2 font-plus-jakarta text-lg font-bold text-gray-900">
								{activeFilter === "all"
									? "No FitChecks yet"
									: `Nothing ${activeFilter === "strong" ? "a strong fit" : "needing attention"} yet`}
							</h3>
							<p className="mx-auto mb-6 max-w-sm text-sm text-gray-600">
								{activeFilter === "all"
									? "Run your first FitCheck from a job listing to see how well you match."
									: "Try another filter to see the rest of your FitChecks."}
							</p>
							{activeFilter === "all" && <Button onClick={() => navigate("/jobs")}>Browse jobs</Button>}
						</div>
					) : (
						<div className="grid gap-4 sm:grid-cols-2">
							{filteredRecords.map((record, index) => (
								<FitCheckCard
									key={`${record.jobId}-${record.createdAt}`}
									record={record}
									index={index}
									answers={answersFor(record.jobId)}
									onViewDetails={() => setSelectedRecord(record)}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
}

export default FitCheckDashboard;
